import { getAsciiName } from './textUtils'

export function getSixMonthsFromNow() {
  const date = new Date()
  date.setMonth(date.getMonth() + 6)
  return date
}

/**
 * Calculate a date that is 14 days before the given date.
 */
export function getFourteenDaysBefore(targetDate: Date): Date {
  const fourteenDaysBefore = new Date(targetDate)
  fourteenDaysBefore.setDate(fourteenDaysBefore.getDate() - 14)
  return fourteenDaysBefore
}

export type CheckInSuggestionType = 'fourteen_days' | 'one_day' | 'six_months'

/**
 * Suggest a check-in date based on a round's open date.
 * Returns 14 days before the open date, or 1 day before if 14 days is in the past,
 * or 6 months from today if even 1 day before is in the past.
 */
export function getSuggestedCheckInForRound(roundOpenDate: Date): {
  date: Date
  suggestionType: CheckInSuggestionType
} {
  const fourteenDaysBefore = getFourteenDaysBefore(roundOpenDate)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  if (fourteenDaysBefore >= today) {
    return { date: fourteenDaysBefore, suggestionType: 'fourteen_days' }
  }

  const oneDayBefore = new Date(roundOpenDate)
  oneDayBefore.setDate(oneDayBefore.getDate() - 1)

  if (oneDayBefore >= today) {
    return { date: oneDayBefore, suggestionType: 'one_day' }
  }

  return { date: getSixMonthsFromNow(), suggestionType: 'six_months' }
}

interface FundingRoundWithOpenDate {
  application_open_date?: string | null
  application_close_date?: string | null
  decision_date?: string | null
}

/**
 * Get the earliest upcoming funding round with an application_open_date.
 * Returns the round or null if none found.
 */
export function getEarliestUpcomingRoundWithOpenDate(
  fundingRounds: FundingRoundWithOpenDate[]
): { application_open_date: string } | null {
  if (!fundingRounds || fundingRounds.length === 0) return null

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const upcomingRounds = fundingRounds
    .filter((round) => {
      if (!round.application_open_date) return false
      const openDate = new Date(round.application_open_date)
      return openDate >= today
    })
    .sort((a, b) => {
      const dateA = new Date(a.application_open_date!)
      const dateB = new Date(b.application_open_date!)
      return dateA.getTime() - dateB.getTime()
    })

  return upcomingRounds.length > 0
    ? { application_open_date: upcomingRounds[0].application_open_date! }
    : null
}

/**
 * Calculate the suggested check-in date based on funding rounds and opportunity settings.
 * Returns the suggested date (14 days before earliest round opens) or falls back to 6 months from now.
 */
export function calculateSuggestedCheckInDate(
  fundingRounds: FundingRoundWithOpenDate[],
  isAlwaysOpen: boolean
): Date {
  // If always open, use 6 months default
  if (isAlwaysOpen) {
    return getSixMonthsFromNow()
  }

  const earliestRound = getEarliestUpcomingRoundWithOpenDate(fundingRounds)

  if (earliestRound) {
    const openDate = new Date(earliestRound.application_open_date)
    return getSuggestedCheckInForRound(openDate).date
  }

  // Fall back to 6 months from now
  return getSixMonthsFromNow()
}

/**
 * Filters the admin tag autocomplete on the Opportunity create/edit pages.
 *
 * Matches the tag TITLE only, never the description. Case-insensitive, and
 * strips macrons so "maori" matches "Iwi/Māori".
 */
export function filterTagOptions<T extends { value: string }>(
  options: T[],
  inputValue: string | undefined,
): T[] {
  const asciiInput = getAsciiName(inputValue || '')
  if (!asciiInput) return options
  return options.filter((option) =>
    getAsciiName(option.value).includes(asciiInput),
  )
}
